/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { required } from 'redux-form-validators';

import { getInnerTransferOptions } from 'client/transfers/_redux';
import { DefaultButton, ReduxSelectField, ReduxTextField } from 'common/components';
import { staticStyles } from './style';

const LpaTransferForm = ({
  toggleModal,
  createTransfer,
  lpaDeal,
  lpaTransferAccounts,
  lpaTransferAccountsAreLoaded,
  isCreateTransfer,
  getLpaTransferAccounts,
  getInnerTransferOptions,
  innerTransferOptions,
  innerTransferOptionsAreLoaded,
  hasWallet,
  history,
  handleSubmit,
  invalid,
  error,
  dispatch,
  submitting,
}) => {
  useEffect(() => {
    if (isCreateTransfer) getLpaTransferAccounts(lpaDeal.id);
  }, [getLpaTransferAccounts, lpaDeal.id]);

  useEffect(() => {
    getInnerTransferOptions({ isLpaTransfer: true });
  }, [getInnerTransferOptions]);

  const accounts = innerTransferOptions
    .filter(item => !item.isBlocked)
    .map(item => ({
      label: `${item.accountType} ${item.login} - ${item.balance.amount}${item.balance.currency}`,
      value: item.id,
    }));

  const recipientEmail = lpaDeal.operation === 0 ? lpaDeal.client.email : lpaDeal.agent.user.email;

  const amountCurrency = lpaDeal.amount.slice(-3);

  const submit = handleSubmit(values => {
    const data = hasWallet
      ? {
          recipient: recipientEmail,
          sendAmount: { amount: lpaDeal.amountToSend.amount, currency: amountCurrency },
          ...values,
        }
      : {
          sendAmount: { amount: lpaDeal.amountToSend.amount, currency: amountCurrency },
          ...values,
        };
    return createTransfer(data, dispatch);
  });

  return (
    <form className="LpaTransferForm">
      <div className="LpaTransferForm__content">
        {hasWallet ? (
          <div>
            <Field
              component={ReduxTextField}
              type="text"
              name="recipient"
              textId="justRecipientEmail"
              disabled
              input={{ value: recipientEmail }}
            />
          </div>
        ) : (
          <>
            <Field
              component={ReduxSelectField}
              name="senderAccount"
              textId="clientAccountSender"
              options={accounts}
              disabled={submitting || !innerTransferOptionsAreLoaded}
              validate={[required()]}
              maxMenuHeight={170}
              searchable
            />
            <Field
              component={ReduxSelectField}
              name="recipientAccount"
              textId="clientAccountRecipient"
              options={lpaTransferAccounts.map(option => ({
                value: option.login,
                label: option.login,
              }))}
              disabled={submitting || !lpaTransferAccountsAreLoaded}
              validate={[required()]}
              maxMenuHeight={170}
              searchable
            />
          </>
        )}
        <Field
          component={ReduxTextField}
          type="text"
          name="sendAmount"
          textId="clientSendAmount"
          disabled
          description={amountCurrency}
          input={{ value: parseInt(lpaDeal.amount, 10) }}
        />
        <Field
          component={ReduxTextField}
          type="text"
          name="receiveAmount"
          textId="justWillReceive"
          disabled
          description={lpaDeal.amountToSend.currency}
          input={{ value: lpaDeal.amountToSend.amount }}
        />
      </div>

      <div className="LpaTransferForm__btns">
        <DefaultButton
          type="submit"
          textId="justConfirm"
          disabled={invalid || submitting}
          loading={submitting}
          onClick={submit}
          filled
        />
        <DefaultButton type="button" textId="justCancel" disabled={submitting} onClick={() => toggleModal()} />
      </div>

      {error && <strong className="formError">{error}</strong>}

      <style jsx>{staticStyles}</style>
    </form>
  );
};

LpaTransferForm.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  createTransfer: PropTypes.func.isRequired,
  lpaDeal: PropTypes.object.isRequired,
  lpaTransferAccounts: PropTypes.array.isRequired,
  lpaTransferAccountsAreLoaded: PropTypes.bool.isRequired,
  isCreateTransfer: PropTypes.bool.isRequired,
  getLpaTransferAccounts: PropTypes.func.isRequired,
  hasWallet: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

LpaTransferForm.defaultProps = {
  error: '',
};

export default compose(
  connect(
    state => ({
      innerTransferOptions: state.client.transfers.innerTransferOptions,
      innerTransferOptionsAreLoaded: state.client.transfers.innerTransferOptionsAreLoaded,
    }),
    {
      getInnerTransferOptions: data => getInnerTransferOptions.request(data),
    }
  ),
  reduxForm({
    form: 'lpa-transfer-form',
    onSubmitSuccess: (result, dispatch, { history, hasWallet }) => {
      if (result) {
        return hasWallet ? history.push('/wallet/internalTransfers') : history.push('/payments?tab=transfers');
      }
    },
  })
)(LpaTransferForm);
export { LpaTransferForm };
